/**
 * HelpPage — "How it works" guide inside the authenticated app.
 * Accessible from the sidebar ? icon. Covers every feature in detail.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './HelpPage.css'

const SECTIONS = [
  { id: 'tree',       label: 'Family tree' },
  { id: 'persons',    label: 'Persons' },
  { id: 'documents',  label: 'Documents' },
  { id: 'origin',     label: 'Origin AI' },
  { id: 'kg',         label: 'Knowledge Graph' },
  { id: 'worlds',     label: 'Worlds & Places' },
  { id: 'journey',    label: 'My Journey' },
  { id: 'stories',    label: 'Story Reports' },
  { id: 'collab',     label: 'Collaboration' },
  { id: 'tiers',      label: 'Plans & tiers' },
  { id: 'gedcom',     label: 'GEDCOM import' },
  { id: 'views',      label: 'Views' },
  { id: 'privacy',    label: 'Privacy' },
]

export default function HelpPage() {
  const navigate = useNavigate()
  const [active, setActive] = useState('tree')

  function scrollTo(id) {
    setActive(id)
    document.getElementById(`help-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="help-page">

      {/* Sticky sidebar nav */}
      <aside className="help-nav">
        <div className="help-nav__title">How it works</div>
        {SECTIONS.map(s => (
          <button
            key={s.id}
            className={`help-nav__item ${active === s.id ? 'is-active' : ''}`}
            onClick={() => scrollTo(s.id)}
          >
            {s.label}
          </button>
        ))}
        <div className="help-nav__divider" />
        <a className="help-nav__link" href="https://genealogic.ai/faq.html" target="_blank" rel="noreferrer">
          FAQ →
        </a>
        <a className="help-nav__link" href="mailto:hello@genealogic.ai">
          Contact us →
        </a>
      </aside>

      {/* Scrollable content */}
      <div className="help-content">

        {/* ── Family tree ──────────────────────────────────────────────── */}
        <section id="help-tree" className="help-section">
          <div className="help-section__badge">01</div>
          <h2 className="help-section__title">Your family tree</h2>
          <p className="help-section__lead">
            The family tree is the foundation of everything. It holds every person, relationship, and date — structured, source-linked, and collaborative.
          </p>

          <div className="help-cards">
            <HelpCard icon="🌳" title="Create a tree">
              From the dashboard, click <strong>New tree</strong>. Give it a name — a family surname, a branch name, or anything that makes sense to you. You can create as many trees as you need: one per family line, or one combined tree.
            </HelpCard>
            <HelpCard icon="🗺️" title="Navigate the tree">
              The tree view shows persons as cards connected by relationship lines. Click any card to open the quick summary panel on the right. Click <strong>Open full page</strong> to see all details, documents, and AI context for that person. Use scroll and pinch-to-zoom to navigate larger trees.
            </HelpCard>
            <HelpCard icon="👥" title="Relationship types">
              Genealogic supports three relationship types: <strong>parent–child</strong>, <strong>partner</strong> (marriages and other partnerships), and implicit sibling (derived from shared parents). A person can have multiple partners and children from different relationships — the tree handles real-world complexity without flattening it.
            </HelpCard>
            <HelpCard icon="📅" title="Timeline view">
              Switch to timeline view to see your family arranged chronologically. Each person appears as a bar spanning their birth-to-death years, overlaid on a shared historical timeline. Useful for spotting which generations overlapped, and which historical events affected multiple branches at once.
            </HelpCard>
          </div>
        </section>

        {/* ── Persons ──────────────────────────────────────────────────── */}
        <section id="help-persons" className="help-section">
          <div className="help-section__badge">02</div>
          <h2 className="help-section__title">Adding &amp; editing persons</h2>
          <p className="help-section__lead">
            Every person in the tree is a structured record. The more you fill in, the richer the historical context Origin can surface.
          </p>

          <div className="help-cards">
            <HelpCard icon="➕" title="Add a person">
              Click <strong>Add person</strong> inside the tree view. Fill in what you know — first name, last name, dates, place. You don't need complete information; a first name alone is enough to start. Save, then link the person to others via the relationship tools.
            </HelpCard>
            <HelpCard icon="✏️" title="Edit a person">
              Open the person's full page and click <strong>Edit</strong>. All fields are editable: name, preferred name (nickname or maiden name used in daily life), birth and death dates with places, occupation, gender, and notes. Partial dates are supported — enter just a year, or "circa 1880".
            </HelpCard>
            <HelpCard icon="🖼️" title="Profile photos">
              Upload a portrait photo from the person's edit panel. Photos are stored privately and shown as the person's avatar throughout the tree and panel views. Supported formats: JPG, PNG, WebP.
            </HelpCard>
            <HelpCard icon="💡" title="Suggest a change">
              Collaborators who don't have editor access can still contribute by submitting a <strong>suggested change</strong> on any person's page. The tree owner reviews and accepts or declines — ensuring nothing overwrites existing data without approval.
            </HelpCard>
          </div>
        </section>

        {/* ── Documents ────────────────────────────────────────────────── */}
        <section id="help-documents" className="help-section">
          <div className="help-section__badge">03</div>
          <h2 className="help-section__title">Documents &amp; evidence</h2>
          <p className="help-section__lead">
            Attach primary source documents to each person. AI analyses them to extract structured facts — names, dates, roles — so you always know what each document proves.
          </p>

          <div className="help-cards">
            <HelpCard icon="📄" title="Upload documents">
              From a person's full page, open the <strong>Documents</strong> tab and drag in a file or click to browse. Supported types: birth certificates, death certificates, marriage records, census pages, military papers, newspaper clippings, letters, photos, and any other scanned PDF or image.
            </HelpCard>
            <HelpCard icon="🔍" title="AI analysis">
              After upload, the document enters AI analysis. Within a minute it extracts: a summary paragraph, the document date and place, and a structured list of all persons mentioned with their roles (subject, parent, witness, spouse, employer, etc.) and occupations where noted.
            </HelpCard>
            <HelpCard icon="✅" title="Reading extracted facts">
              Extracted facts appear inline in the person's quick panel — click the expand toggle on any analysed document to see them. A green ✓ badge marks documents that have been fully analysed. A confidence indicator flags results that may be uncertain and worth reviewing manually.
            </HelpCard>
            <HelpCard icon="🔒" title="Storage &amp; privacy">
              Documents are stored in encrypted private cloud storage (AWS S3, EU region). They are never shared publicly and never used to train AI models. Only you and invited collaborators can access them.
            </HelpCard>
          </div>
        </section>

        {/* ── Origin AI ────────────────────────────────────────────────── */}
        <section id="help-origin" className="help-section">
          <div className="help-section__badge">04</div>
          <h2 className="help-section__title">Origin AI</h2>
          <p className="help-section__lead">
            Origin is the historical intelligence layer. It places each ancestor in their real world — what they lived through, what records document them, what their occupation meant in that era.
          </p>

          <div className="help-cards">
            <HelpCard icon="🏛️" title="Historical context">
              Open any person's panel and scroll to the <strong>Origin</strong> section. Suggestion chips appear based on the person's data: their birthplace, occupation, and era. Click a chip — or the research button — to stream a detailed historical briefing. Results are cached, so repeat views are instant.
            </HelpCard>
            <HelpCard icon="💬" title="What Origin covers">
              A typical Origin response covers: <em>daily life</em> (housing, food, work, transport), <em>population and economy</em> (industries, class, migration), <em>key events</em> (wars, crises, transformations), and <em>records that exist</em> (specific archives, collections, and date ranges to search for this person).
            </HelpCard>
            <HelpCard icon="🌐" title="Public era search">
              The public Origin search at <a href="https://genealogic.ai" target="_blank" rel="noreferrer" className="help-link">genealogic.ai</a> is open to anyone. Type a place and era — <em>"Amsterdam 1940s"</em>, <em>"rural Ireland 1850s"</em> — and Origin streams a historical briefing instantly. Each result gets a permanent URL you can share.
            </HelpCard>
            <HelpCard icon="📌" title="What Origin doesn't do">
              Origin does not invent biographical facts about your specific ancestor. It provides documented historical context — what was true for people in that place and time — not a fictional life story. When something is uncertain, it says so explicitly.
            </HelpCard>
          </div>
        </section>

        {/* ── Knowledge Graph ─────────────────────────────────────────── */}
        <section id="help-kg" className="help-section">
          <div className="help-section__badge">05</div>
          <h2 className="help-section__title">Knowledge Graph</h2>
          <p className="help-section__lead">
            The Knowledge Graph is our open historical record index — tens of thousands of civil-register, birth, marriage, and death records harvested from Wikidata, GEDCOM files, and European archives. It's the foundation behind automatic match suggestions on every person in your tree.
          </p>

          <div className="help-cards">
            <HelpCard icon="🔍" title="Browse the archive">
              Open <a href="https://genealogic.ai/records" target="_blank" rel="noreferrer" className="help-link">genealogic.ai/records</a> to search the full index — filter by surname, place, year range, or record type. Every record has its own permanent URL indexed by Google.
            </HelpCard>
            <HelpCard icon="✨" title="Automatic matches">
              Every person page shows an <strong>Archive matches</strong> panel with candidate records from the Knowledge Graph. Matching is based on surname, first-name prefix, and a ±5-year birth window. Free sees up to 3 matches, Plus 20, Family and Archive 100.
            </HelpCard>
            <HelpCard icon="⬇️" title="Claim an ancestor">
              On any record detail page, click <strong>Claim this ancestor</strong>. A new person is created in your chosen tree populated from the record's fields, with the source (archive, collection, URL) kept as a citation in notes.
            </HelpCard>
            <HelpCard icon="📜" title="Open data">
              The full Knowledge Graph is published under CC-BY-SA-4.0 at <code>/api/kg/manifest</code> — NDJSON streams for persons, places, events, and records. Only historical persons; no living-person data ever leaves the private layer.
            </HelpCard>
          </div>
        </section>

        {/* ── Worlds & Places ─────────────────────────────────────────── */}
        <section id="help-worlds" className="help-section">
          <div className="help-section__badge">06</div>
          <h2 className="help-section__title">Worlds &amp; Places</h2>
          <p className="help-section__lead">
            Two views of where and when your ancestors actually lived. Worlds is the curated, immersive takeover; Places is the geographic dashboard.
          </p>

          <div className="help-cards">
            <HelpCard icon="🌍" title="Their World takeovers">
              When a person overlaps a curated World by place and era — <em>Rotterdam 1940–45</em>, <em>Watersnoodramp 1953</em>, <em>Limburg coal mines</em>, <em>Dust Bowl</em> — their profile gets a full-bleed takeover. Narrative, period images, newspaper snippets from Delpher, your own uploaded documents interleaved by date, and prompts to fill in missing facts.
            </HelpCard>
            <HelpCard icon="🗺️" title="Places map">
              Switch to the <strong>Places</strong> tab for a Leaflet map of everywhere your tree has lived, with a time slider and event-type colours. Birth and death places feed the map directly — no document upload required.
            </HelpCard>
            <HelpCard icon="📰" title="Newspaper evidence">
              Worlds pull real newspaper snippets from <strong>Delpher</strong> (KB open archive) for the person's actual town and year range. Cached for 30 days per place+date window.
            </HelpCard>
            <HelpCard icon="💡" title="Enrichment prompts">
              Each World ends with fill-in prompts — <em>"Did they leave before the flood?"</em> — whose answers update the person and refresh the World fit. Tagged facts feed future matches.
            </HelpCard>
          </div>
        </section>

        {/* ── My Journey ─────────────────────────────────────────────── */}
        <section id="help-journey" className="help-section">
          <div className="help-section__badge">07</div>
          <h2 className="help-section__title">My Journey</h2>
          <p className="help-section__lead">
            My Journey is your personal quest list — the highest-leverage next action per person, ranked. The sidebar badge shows how many quests await.
          </p>

          <div className="help-cards">
            <HelpCard icon="🎯" title="Ranked tasks">
              Every quest has a kind — <em>unlock World</em>, <em>link entity</em>, <em>upload document</em>, <em>add birth place</em>, <em>enter World</em>. The system picks the one most likely to unlock new context for each person and surfaces it on the dashboard.
            </HelpCard>
            <HelpCard icon="✋" title="Dismiss what you can't solve">
              Click <strong>Can't resolve</strong> on any task to dismiss it permanently with a reason. Dismissals persist across sessions so the quest list stays honest.
            </HelpCard>
          </div>
        </section>

        {/* ── Story Reports & Family Archive ────────────────────────── */}
        <section id="help-stories" className="help-section">
          <div className="help-section__badge">08</div>
          <h2 className="help-section__title">Story Reports &amp; Family Archive</h2>
          <p className="help-section__lead">
            Two PDF deliverables that turn tree data into something you can hold, print, and share.
          </p>

          <div className="help-cards">
            <HelpCard icon="📖" title="Story Report (one person)">
              A 15–20 page biography of one person: profile, family, AI-generated historical context, life story, timeline, events they lived through, World excerpts, Delpher newspaper snippets, primary sources, and a section on what remains unknown. €9.99 one-off, or 1/month free on Family.
            </HelpCard>
            <HelpCard icon="📚" title="Family Archive (whole tree)">
              A PDF book of your entire tree, once every 30 days. Family-tier perk. Generated on demand, delivered as a signed download.
            </HelpCard>
            <HelpCard icon="🎬" title="Photo animation">
              Upload a portrait; the Higgsfield engine generates a short lifelike animation (subtle head turn, smile, historic-wind preset). Family and Archive tiers only. Stored privately in your S3 bucket, attached to the person alongside the source photo.
            </HelpCard>
            <HelpCard icon="💳" title="Buying &amp; billing">
              Purchases are handled via Stripe Checkout. Manage subscription, view invoices, and download past Story Reports from the <strong>Billing</strong> page in the sidebar.
            </HelpCard>
          </div>
        </section>

        {/* ── Collaboration ─────────────────────────────────────────────── */}
        <section id="help-collab" className="help-section">
          <div className="help-section__badge">09</div>
          <h2 className="help-section__title">Collaboration</h2>
          <p className="help-section__lead">
            Invite family members to contribute. Each collaborator gets editor or viewer access. You stay in control.
          </p>

          <div className="help-cards">
            <HelpCard icon="🔗" title="Invite links">
              Inside any tree, click the <strong>Invite</strong> button (person icon) in the toolbar. Generate an invite link — valid for 7 days — and share it via the Copy, WhatsApp, or Email buttons. Anyone with the link can join the tree with the permission level you set.
            </HelpCard>
            <HelpCard icon="📧" title="Email invites">
              Optionally enter an email address when generating an invite link. The platform sends the invite directly, and the person appears in your pending invites list. You can revoke any pending invite before it's accepted.
            </HelpCard>
            <HelpCard icon="🔑" title="Permission levels">
              <strong>Editor</strong> — can add persons, edit existing persons, upload documents, and add relationships. <strong>Viewer</strong> — can browse the full tree and see all details, but cannot make changes. You can change a member's role or remove them at any time from the Invite &amp; members panel.
            </HelpCard>
            <HelpCard icon="✍️" title="Suggested changes">
              Viewers (and editors who prefer a review workflow) can submit suggested changes on any person. The tree owner sees these in the suggestions panel and accepts or declines them individually. Nothing is overwritten without explicit approval.
            </HelpCard>
          </div>
        </section>

        {/* ── Plans & tiers ──────────────────────────────────────────── */}
        <section id="help-tiers" className="help-section">
          <div className="help-section__badge">10</div>
          <h2 className="help-section__title">Plans &amp; tiers</h2>
          <p className="help-section__lead">
            Free is generous. Paid tiers unlock unlimited persons, AI research, co-researchers, and PDF deliverables.
          </p>

          <div className="help-cards">
            <HelpCard icon="🆓" title="Free">
              Up to <strong>50 persons per tree</strong>, <strong>5 AI document scans per month</strong>, GEDCOM import and export, public Knowledge Graph browse, 3 archive matches per person. Always free.
            </HelpCard>
            <HelpCard icon="➕" title="Plus — €7.99/mo (or €79.90/yr, save ~17%)">
              <strong>Unlimited persons</strong> per tree, unlimited AI document scans, all Origin AI research (context, story, knowledge, chat), 20 archive matches per person. No co-researchers.
            </HelpCard>
            <HelpCard icon="👪" title="Family — €14.99/mo">
              Everything in Plus, plus <strong>2 co-researchers per tree</strong>, side-by-side edit review, <strong>100 archive matches per person</strong>, <strong>1 Story Report/month included</strong>, annual Family Archive PDF, photo-to-video animation.
            </HelpCard>
            <HelpCard icon="🏛️" title="Archive (coming soon)">
              Uncapped co-researchers, priority capsule authoring, early access to new countries' record harvests, bulk Story Report generation.
            </HelpCard>
          </div>
        </section>

        {/* ── GEDCOM ────────────────────────────────────────────────────── */}
        <section id="help-gedcom" className="help-section">
          <div className="help-section__badge">11</div>
          <h2 className="help-section__title">GEDCOM import</h2>
          <p className="help-section__lead">
            Already have a family tree in another app? Import it in seconds using the GEDCOM standard.
          </p>

          <div className="help-cards">
            <HelpCard icon="📥" title="What is GEDCOM?">
              GEDCOM (.ged) is the universal format for genealogy data, supported by Ancestry, MyHeritage, Family Tree Maker, Gramps, MacFamilyTree, and most other genealogy apps. Export your tree from any of these as a .ged file, then import it into Genealogic.
            </HelpCard>
            <HelpCard icon="⬆️" title="How to import">
              Inside your tree view, click the <strong>Import GEDCOM</strong> button in the toolbar. Drag in or browse for your .ged file. The import runs immediately — all persons and relationships are created. Large trees (thousands of persons) typically import in under 30 seconds.
            </HelpCard>
            <HelpCard icon="🔗" title="After import">
              All persons land in the tree with their names, dates, places, and relationships intact. You can then enrich each person: add documents, upload photos, and use Origin AI to surface historical context. GEDCOM imports don't overwrite existing persons — they add to the tree.
            </HelpCard>
            <HelpCard icon="⚠️" title="GEDCOM limitations">
              GEDCOM doesn't carry documents, photos, or AI analysis — those live on the Genealogic platform. Some very old GEDCOM files use non-standard character encodings; if you hit an issue, contact us and we'll help.
            </HelpCard>
          </div>
        </section>

        {/* ── Views ─────────────────────────────────────────────────────── */}
        <section id="help-views" className="help-section">
          <div className="help-section__badge">12</div>
          <h2 className="help-section__title">Views &amp; visualisations</h2>
          <p className="help-section__lead">
            Four ways to explore your family — each one reveals a different dimension of the story.
          </p>

          <div className="help-cards">
            <HelpCard icon="🌳" title="Tree view">
              The default generational view. Persons are cards arranged top-to-bottom by generation, connected by parent–child and partner lines. Click any card to open the quick summary panel. Best for understanding the family structure and navigating between relatives.
            </HelpCard>
            <HelpCard icon="🕸️" title="Network map">
              A force-directed graph of every connection: not just the direct family line, but in-laws, cousins, and witnesses on documents. Reveals the full social network your ancestors lived in. Useful for spotting unexpected connections and understanding how your family related to its community.
            </HelpCard>
            <HelpCard icon="🗺️" title="Geo map">
              <em>(Coming soon)</em> Where did your family live, and when? Migration lines on a map, location heatmaps by decade, and the question every family asks: why did they leave? Births, deaths, and significant events plotted geographically.
            </HelpCard>
            <HelpCard icon="📅" title="Timeline">
              All persons on a shared chronological axis, their lifespans shown as bars. Overlay key historical events to see which wars, famines, or upheavals your family lived through. Makes multi-generational patterns — migration waves, longevity patterns — immediately visible.
            </HelpCard>
          </div>
        </section>

        {/* ── Privacy ───────────────────────────────────────────────────── */}
        <section id="help-privacy" className="help-section">
          <div className="help-section__badge">13</div>
          <h2 className="help-section__title">Privacy &amp; data</h2>
          <p className="help-section__lead">
            Your family data is private by default. Living persons are protected automatically. You stay in control of everything.
          </p>

          <div className="help-cards">
            <HelpCard icon="🛡️" title="Living persons">
              Genealogic automatically identifies living persons (born after 1920 without a recorded death date) and treats them as private. In public search results they appear only as "Private record" — no name, dates, or personal details are shown to anyone outside your tree.
            </HelpCard>
            <HelpCard icon="🔐" title="Data storage">
              Your tree data is stored in a PostgreSQL database on EU-based servers. Documents and photos are stored in encrypted private cloud storage (AWS S3, eu-central-1 region). All data is encrypted at rest and in transit.
            </HelpCard>
            <HelpCard icon="🚫" title="No AI training">
              Your family data and documents are never used to train AI models — by Genealogic or any third party. AI features process your data only to generate responses for you, in the moment, and do not store or share those responses.
            </HelpCard>
            <HelpCard icon="📤" title="Export &amp; deletion">
              You can request a full export or complete deletion of all your data at any time. Email <a href="mailto:hello@genealogic.ai" className="help-link">hello@genealogic.ai</a> and we'll process your request within 30 days, as required by GDPR.
            </HelpCard>
          </div>
        </section>

        {/* Footer CTA */}
        <div className="help-footer-cta">
          <div className="help-footer-cta__text">
            Still have questions?
          </div>
          <a href="https://genealogic.ai/faq.html" target="_blank" rel="noreferrer" className="help-footer-cta__link">
            Read the full FAQ →
          </a>
          <a href="mailto:hello@genealogic.ai" className="help-footer-cta__link">
            Email us →
          </a>
        </div>

      </div>
    </div>
  )
}

function HelpCard({ icon, title, children }) {
  return (
    <div className="help-card">
      <div className="help-card__icon">{icon}</div>
      <div>
        <div className="help-card__title">{title}</div>
        <div className="help-card__body">{children}</div>
      </div>
    </div>
  )
}
